import React from "react";

import ActionBar from "./ActionBar";
import { cn } from "./cn";
import type { Action } from "./action";

export type Column<T> = {
  /** Column header label. */
  header: React.ReactNode;
  /** Render a cell given the row. */
  cell: (row: T) => React.ReactNode;
  align?: "left" | "right" | "center";
  /** Tailwind class for the column's explicit width (e.g. "w-32"). */
  className?: string;
  /** Use when you want the header to hide but the cell still render. */
  srOnlyHeader?: boolean;
};

type Props<T> = {
  columns: Column<T>[];
  rows: T[] | null | undefined;
  rowKey: (row: T) => string | number;
  /** Right-aligned per-row action cluster; omit to skip the Actions column. */
  rowActions?: (row: T) => Action[];
  loading?: boolean;
  /** Custom empty state — string, element, or fall back to default copy. */
  empty?: React.ReactNode;
  className?: string;
};

function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowActions,
  loading,
  empty,
  className,
}: Props<T>) {
  const showActionsCol = Boolean(rowActions);
  const colCount = columns.length + (showActionsCol ? 1 : 0);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-gray-200 bg-white",
        className,
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
              {columns.map((c, i) => (
                <th
                  key={i}
                  className={cn(
                    "px-4 py-2.5",
                    c.align === "right"
                      ? "text-right"
                      : c.align === "center"
                      ? "text-center"
                      : "text-left",
                    c.className,
                    c.srOnlyHeader ? "sr-only" : undefined,
                  )}
                >
                  {c.header}
                </th>
              ))}
              {showActionsCol ? (
                <th className="px-4 py-2.5 text-right">Actions</th>
              ) : null}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <LoadingRows columns={colCount} />
            ) : !rows ? (
              <LoadingRows columns={colCount} />
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-4 py-12 text-center text-[12.5px] text-gray-500"
                >
                  {empty ?? "Nothing to show here yet."}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className="transition-colors hover:bg-gray-50/60"
                >
                  {columns.map((c, i) => (
                    <td
                      key={i}
                      className={cn(
                        "px-4 py-3 align-middle",
                        c.align === "right"
                          ? "text-right"
                          : c.align === "center"
                          ? "text-center"
                          : "text-left",
                        c.className,
                      )}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                  {showActionsCol ? (
                    <td className="px-4 py-2.5 text-right align-middle">
                      <div className="flex justify-end">
                        <ActionBar
                          actions={rowActions!(row)}
                          density="compact"
                        />
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const LoadingRows: React.FC<{ columns: number }> = ({ columns }) => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <tr key={i}>
        {Array.from({ length: columns }).map((__, j) => (
          <td key={j} className="px-4 py-3">
            <div className="h-3 w-28 animate-pulse rounded bg-gray-100" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

export default DataTable;
